// views/DeliveryFee/feeUiKit.js
// ─────────────────────────────────────────────────────────────────────────────
// Shared design tokens + tiny presentational helpers for the Delivery-Fee
// Transparency UI. These mirror the Power-BI–inspired palette already used in
// DeliveryFeeSettings.js so the new transparency screens look native to the
// dashboard. Kept framework-light (inline styles + react-icons) to match the
// surrounding views — NO new UI library is introduced.
// ─────────────────────────────────────────────────────────────────────────────

// ── Design tokens (shared with DeliveryFeeSettings) ──────────────────────────
export const C = {
  bg:           "#f4f6fb",
  panel:        "#ffffff",
  header:       "#1a2b4a",
  accent:       "#e63757",
  accentSoft:   "#fff0f3",
  accentBorder: "#f9c8d0",
  blue:         "#2c7be5",
  blueSoft:     "#eaf2ff",
  teal:         "#00b69b",
  tealSoft:     "#e6faf7",
  orange:       "#f6961e",
  orangeSoft:   "#fff4e6",
  purple:       "#7048e8",
  purpleSoft:   "#f1edff",
  text:         "#1a2b4a",
  textMid:      "#4a5568",
  textLight:    "#8898aa",
  border:       "#e2e8f0",
  inputBg:      "#f8faff",
  shadow:       "0 2px 12px rgba(26,43,74,0.08)",
  shadowHover:  "0 4px 20px rgba(26,43,74,0.14)",
  radius:       "10px",
  radiusSm:     "6px",
};

// ── Zone metadata (urban / peri-urban / upcountry) ───────────────────────────
// Keys match the engine's zone identifiers exactly (see services/deliveryZones.js).
export const ZONE_META = {
  urban:      { label: "Urban",       color: C.blue,   soft: C.blueSoft,   desc: "Kampala metro — short legs, traffic." },
  peri_urban: { label: "Peri-urban",  color: C.teal,   soft: C.tealSoft,   desc: "Wakiso / Mukono fringe — longer legs." },
  upcountry:  { label: "UPCOUNTRY",   color: C.orange, soft: C.orangeSoft, desc: "Inter-city / regional — long highway." },
};

export const zoneMeta = (zone) =>
  ZONE_META[zone] || { label: String(zone || "—"), color: C.textMid, soft: "#f0f2f5", desc: "" };

// ── Complexity metadata (mirrors DeliveryFeeSettings) ────────────────────────
export const COMPLEXITY_META = {
  standard:  { label: "Standard",  color: C.textMid, soft: "#f0f2f5"    },
  moderate:  { label: "Moderate",  color: C.blue,    soft: C.blueSoft   },
  difficult: { label: "Difficult", color: C.orange,  soft: C.orangeSoft },
  premium:   { label: "Premium",   color: C.accent,  soft: C.accentSoft },
};

// ── Flag metadata — turns raw engine flags into friendly badges ──────────────
// `tone` selects a colour; `label` is the human copy; `hint` explains it.
// Unknown flags fall back to a neutral grey badge with the prettified key.
export const FLAG_META = {
  margin_floor_applied: {
    tone: "orange",
    label: "Margin floor applied",
    hint: "The computed fare dipped below the cost-aware floor, so the floor was used instead. Protects against loss-making trips.",
  },
  min_fee_applied: {
    tone: "blue",
    label: "Minimum fee applied",
    hint: "The trip was short enough that the zone's minimum fee kicked in.",
  },
  soft_cap_applied: {
    tone: "teal",
    label: "Soft cap applied",
    hint: "The fare was trimmed down to the zone's soft cap so the customer is not overcharged on long legs.",
  },
  rider_underpaid: {
    tone: "accent",
    label: "Rider underpaid",
    hint: "The rider's share falls below the healthy earnings threshold for this trip — review pricing or the split.",
  },
  surge_active: {
    tone: "accent",
    label: "Surge active",
    hint: "A surge multiplier above 1.0× is currently being applied.",
  },
  upcountry_uncapped: {
    tone: "orange",
    label: "Uncapped (upcountry)",
    hint: "Upcountry legs have no soft cap — only the margin floor governs the minimum.",
  },
};

export const flagMeta = (flag) =>
  FLAG_META[flag] || {
    tone: "muted",
    label: String(flag || "")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (ch) => ch.toUpperCase()),
    hint: "",
  };

const TONE_COLORS = {
  blue:   { color: C.blue,   soft: C.blueSoft   },
  teal:   { color: C.teal,   soft: C.tealSoft   },
  orange: { color: C.orange, soft: C.orangeSoft },
  accent: { color: C.accent, soft: C.accentSoft },
  purple: { color: C.purple, soft: C.purpleSoft },
  muted:  { color: C.textMid, soft: "#f0f2f5"   },
};

export const toneColors = (tone) => TONE_COLORS[tone] || TONE_COLORS.muted;

// ── Formatting helpers ───────────────────────────────────────────────────────
export const formatUGX = (n) =>
  n !== undefined && n !== null && !Number.isNaN(Number(n))
    ? `UGX ${Math.round(Number(n)).toLocaleString()}`
    : "—";

// Signed amount — used in the waterfall so additions read "+ UGX x".
export const formatSignedUGX = (n) => {
  if (n === undefined || n === null || Number.isNaN(Number(n))) return "—";
  const v = Math.round(Number(n));
  if (v === 0) return "UGX 0";
  return `${v > 0 ? "+ " : "− "}UGX ${Math.abs(v).toLocaleString()}`;
};

export const formatKm = (n) =>
  n !== undefined && n !== null && !Number.isNaN(Number(n))
    ? `${Number(n).toFixed(1)} km`
    : "—";

export const formatMin = (n) =>
  n !== undefined && n !== null && !Number.isNaN(Number(n))
    ? `${Math.round(Number(n))} min`
    : "—";

export const formatMultiplier = (n) =>
  n !== undefined && n !== null && !Number.isNaN(Number(n))
    ? `${Number(n).toFixed(2)}×`
    : "1.00×";

export const formatPct = (n) =>
  n !== undefined && n !== null && !Number.isNaN(Number(n))
    ? `${Number(n).toFixed(1)}%`
    : "—";

// ── Shared inline-style atoms ────────────────────────────────────────────────
export const panelStyle = {
  background: C.panel,
  borderRadius: C.radius,
  boxShadow: C.shadow,
  marginBottom: 24,
  overflow: "hidden",
  border: `1px solid ${C.border}`,
};

export const panelHeadStyle = (accentColor) => ({
  background: `linear-gradient(90deg, ${accentColor}12 0%, transparent 100%)`,
  borderBottom: `1px solid ${C.border}`,
  padding: "14px 20px",
  display: "flex",
  alignItems: "center",
  gap: 10,
});

export const panelHeadDot = (color) => ({
  width: 10,
  height: 10,
  borderRadius: "50%",
  background: color,
  flexShrink: 0,
});

export const panelTitleStyle = {
  fontSize: 13,
  fontWeight: 700,
  color: C.text,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  marginBottom: 0,
};

export const panelSubStyle = {
  fontSize: 11,
  color: C.textLight,
  marginLeft: "auto",
  display: "flex",
  alignItems: "center",
  gap: 4,
};

export const inputStyle = {
  background: C.inputBg,
  border: `1px solid ${C.border}`,
  borderRadius: C.radiusSm,
  fontSize: 13,
  color: C.text,
  padding: "7px 11px",
  width: "100%",
  outline: "none",
  transition: "border-color 0.15s",
};

export const fieldLabelStyle = {
  fontSize: 11,
  fontWeight: 700,
  color: C.textMid,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  marginBottom: 4,
  display: "block",
};

export const badgeStyle = (color, soft) => ({
  background: soft,
  color,
  border: `1px solid ${color}30`,
  borderRadius: 20,
  padding: "2px 10px",
  fontSize: 11,
  fontWeight: 700,
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
});
